import React from "react";

import "./css/HomePage.css";

function ChipList({ items }) {
  return (
    <div className="tech-list">
      {items.map((item) => (
        <span key={item} className="chip">
          {item}
        </span>
      ))}
    </div>
  );
}

const skills = [
  {
    label: "Languages",
    items: [
      "Python 3",
      "Java",
      "TypeScript",
      "JavaScript",
      "Rust",
      "C++",
      "Go",
      "C#",
      "SQL",
    ],
  },
  {
    label: "Software Engineering",
    items: [
      "Bug fixing / root cause analysis",
      "Feature implementation",
      "Refactoring",
      "Performance optimization",
      "Unit & automated tests",
      "Golden / reference implementations",
    ],
  },
  {
    label: "Algorithms & Systems",
    items: [
      "Data structures",
      "Algorithms",
      "Performance tuning",
      "Distributed systems",
      "Scalability",
      "API / service design",
    ],
  },
  {
    label: "MCP & AI Agents",
    items: [
      "MCP tool servers",
      "Agentic workflows",
      "LangChain / LangGraph",
      "LLM tool use",
      "AI coding agents",
      "Code evaluation",
    ],
  },
  {
    label: "Cloud & Delivery",
    items: [
      "AWS",
      "Azure",
      "GCP",
      "Docker",
      "Kubernetes",
      "Terraform",
      "CI/CD",
      "Linux",
      "Event-driven services",
      "Microservices",
    ],
  },
];

export default function HomePage() {
  return (
    <div className="home-container">
      <h1 className="title">Summary</h1>
      <div className="content">
        <h2 className="headline">
          Senior Software Engineer
        </h2>
        <p className="focus-line">
          Python · Java · TypeScript · Rust · C++ · Cloud · Algorithms · MCP ·
          Debugging &amp; Performance
        </p>
        <p className="availability">
          Portland, Oregon, United States · Remote · Contractor
        </p>
        <p className="resume-link-row">
          <a className="resume-link" href="/resume">
            View full resume
          </a>
        </p>

        <p>
          Senior Software Engineer with 15+ years of production experience
          across bug fixing, feature implementation, codebase refactoring, and
          performance optimization. Strong in Python, Java,
          TypeScript/JavaScript, Rust, and C++, with additional Go, plus a CS
          foundation (B.S. and M.S.) in algorithms, data structures, and
          systems. Implemented a Rust program at PacteraEdge to process CSV
          data files as part of operational data workflows.
        </p>
        <p>
          Recent work includes building Model Context Protocol (MCP) servers
          and tools so AI agents can call real systems through structured
          contracts; debugging agent and tool failures; and producing
          maintainable, testable software that can serve as a reference
          solution. Previously evaluated AI-generated code for correctness,
          quality, maintainability, and performance (DataAnnotation.tech)
          across Python, JavaScript, C++, C#, and SQL.
        </p>
        <p>
          Comfortable designing reproducible engineering work: clear problem
          statements, deterministic checks (tests, expected outputs, root cause analysis), and
          golden implementations. Experienced on large-scale distributed
          codebases (Bank of America, Adidas, Nike, UPS) and on remote,
          cross-functional teams with written design, code review, and
          production support.
        </p>

        <h3 className="section-heading">Core Skills</h3>
        {skills.map((group) => (
          <div key={group.label} className="skill-group">
            <h4 className="skill-label">{group.label}</h4>
            <ChipList items={group.items} />
          </div>
        ))}

        <h3 className="section-heading">Education</h3>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Degree</th>
              <th>University</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Master&apos;s degree in Computer Science</td>
              <td>Portland State University</td>
              <td>2017 – 2020</td>
            </tr>
            <tr>
              <td>Graduate Certificate in Computer Science</td>
              <td>Portland State University</td>
              <td>2020</td>
            </tr>
            <tr>
              <td>Bachelor of Science in Computer Science</td>
              <td>Portland State University</td>
              <td>2006 – 2009</td>
            </tr>
          </tbody>
        </table>

        <h3 className="section-heading">Certifications</h3>
        <ul className="cert-list">
          <li>Production AI Agents with LangChain + LangGraph — 2026 (Udemy)</li>
          <li>Oracle Certified Professional — Java SE6 Programmer</li>
          <li>Certified Deep Learning Specialization</li>
          <li>HashiCorp Vault Certification</li>
        </ul>
      </div>
    </div>
  );
}
